import { useState, type CSSProperties, type ReactElement } from 'react';
import { MARCAS, Marca } from '@selflevel/ui';
import { MAX_AREAS_EXTRA, POINTS, WEEKDAY_NAMES, type Cadence } from '@selflevel/domain';
import { useAreas } from '../store/hooks';
import { archiveHabit, upsertHabit } from '../store/mutations';
import type { Habit, LifeArea } from '../store/db';
import { Segmented, Select, Sheet } from '../ui/primitivas';

/**
 * Crear o editar un hábito.
 *
 * El formulario pide **nombre y ya está**. Área, marca y cadencia tienen valor
 * por defecto y se pueden cambiar sin salir de la hoja. Cada campo obligatorio
 * de más es gente que no llega a tener su primer hábito, y en una app así el día
 * uno es el único día que importa.
 *
 * El formulario vive en un componente aparte que solo se monta cuando la hoja
 * está abierta, con `key`. Así el estado inicial sale de `useState` y no hace
 * falta un `useEffect` que copie las props a estado cada vez que se abre — que
 * es un render de más y una fuente de desajustes.
 */
export function EditorHabito({
  open,
  onClose,
  habit,
  areaInicial,
}: {
  open: boolean;
  onClose: () => void;
  habit?: Habit | undefined;
  /** El área con la que abre un hábito NUEVO. Desde la ficha de un área, esa. */
  areaInicial?: string | undefined;
}): ReactElement {
  const areas = useAreas();

  return (
    <Sheet open={open} onClose={onClose} title={habit ? 'Editar hábito' : 'Nuevo hábito'}>
      <Formulario
        key={habit?.id ?? `nuevo:${areaInicial ?? ''}`}
        habit={habit}
        areas={areas ?? []}
        areaInicial={areaInicial}
        onClose={onClose}
      />
    </Sheet>
  );
}

function Formulario({
  habit,
  areas,
  areaInicial,
  onClose,
}: {
  habit: Habit | undefined;
  areas: readonly LifeArea[];
  areaInicial?: string | undefined;
  onClose: () => void;
}): ReactElement {
  const [name, setName] = useState(habit?.name ?? '');
  const [areaId, setAreaId] = useState(habit?.areaId ?? areaInicial ?? areas[0]?.id ?? '');
  const [marca, setMarca] = useState(habit?.marca ?? 'deporte');
  const [kind, setKind] = useState<Cadence['kind']>(habit?.cadence.kind ?? 'daily');
  const [days, setDays] = useState<number[]>(
    habit?.cadence.kind === 'weekdays' ? [...habit.cadence.days] : [1, 2, 3, 4, 5],
  );
  const [times, setTimes] = useState(
    habit?.cadence.kind === 'timesPerWeek' || habit?.cadence.kind === 'timesPerMonth'
      ? habit.cadence.times
      : 3,
  );
  const [target, setTarget] = useState(
    habit?.target === null || habit === undefined ? '' : String(habit.target),
  );
  const [unit, setUnit] = useState(habit?.unit ?? '');
  const [showMarcas, setShowMarcas] = useState(false);
  const [descripcion, setDescripcion] = useState(habit?.description ?? '');
  const [extras, setExtras] = useState<string[]>(habit?.extraAreaIds ?? []);
  // Las extra nunca incluyen la principal: si se cambia la principal a una extra, sale de ellas.
  const extrasVigentes = extras.filter((a) => a !== areaId);
  const alternarExtra = (id: string) =>
    setExtras((actuales) =>
      actuales.includes(id)
        ? actuales.filter((a) => a !== id)
        : [...actuales.filter((a) => a !== areaId), id].slice(-MAX_AREAS_EXTRA),
    );

  const cadence: Cadence =
    kind === 'daily'
      ? { kind: 'daily' }
      : kind === 'weekdays'
        ? { kind: 'weekdays', days }
        : kind === 'timesPerWeek'
          ? { kind: 'timesPerWeek', times }
          : { kind: 'timesPerMonth', times };

  const guardar = async () => {
    if (name.trim() === '' || areaId === '') return;
    await upsertHabit({
      ...(habit ? { id: habit.id } : {}),
      areaId,
      name,
      marca,
      cadence,
      target: target === '' ? null : Number(target),
      unit: unit.trim() === '' ? null : unit.trim(),
      description: descripcion,
      extraAreaIds: extrasVigentes,
    });
    onClose();
  };

  return (
    <div className="stack stack--loose">
      <div className="field">
        <label htmlFor="habito-nombre">¿Qué quieres hacer?</label>
        <div className="row">
          <button
            type="button"
            className="btn btn--icon"
            onClick={() => setShowMarcas((v) => !v)}
            aria-label="Cambiar la marca"
            aria-expanded={showMarcas}
          >
            <Marca id={marca} size={22} />
          </button>
          <input
            id="habito-nombre"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Caminar, leer, meditar…"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="habito-descripcion">
          Descripción <span className="t-dim">· opcional</span>
        </label>
        <input
          id="habito-descripcion"
          className="input"
          value={descripcion}
          maxLength={140}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="30 minutos en la bici, sin el móvil…"
          autoComplete="off"
        />
      </div>

      {showMarcas ? (
        <div className="rejilla-marcas" role="group" aria-label="Marcas">
          {MARCAS.map((m) => (
            <button
              key={m.id}
              type="button"
              className={m.id === marca ? 'btn coin--accent' : 'btn'}
              aria-pressed={m.id === marca}
              onClick={() => {
                setMarca(m.id);
                setShowMarcas(false);
              }}
              aria-label={m.n}
              title={m.n}
            >
              <Marca id={m.id} size={22} />
            </button>
          ))}
        </div>
      ) : null}

      {areas.length > 1 ? (
        <div className="field">
          <label htmlFor="habito-area">Área</label>
          <Select
            id="habito-area"
            label="Área"
            value={areaId}
            onChange={setAreaId}
            options={areas.map((area) => ({ value: area.id, label: area.name }))}
          />
        </div>
      ) : null}

      {areas.length > 1 ? (
        <div className="field">
          <span className="t-body-strong" id="habito-extras">
            También trabaja <span className="t-dim">· opcional</span>
          </span>
          <div className="habito-extras" role="group" aria-labelledby="habito-extras">
            {areas
              .filter((area) => area.id !== areaId)
              .map((area) => {
                const si = extrasVigentes.includes(area.id);
                return (
                  <button
                    key={area.id}
                    type="button"
                    className={`chip habito-extras__area`}
                    aria-pressed={si}
                    style={{ ['--area']: `var(${area.colorToken})` } as CSSProperties}
                    onClick={() => alternarExtra(area.id)}
                  >
                    {area.name}
                  </button>
                );
              })}
          </div>
          <span className="t-sm t-dim">
            Hasta {MAX_AREAS_EXTRA}. Hacerlo suma también {POINTS.habitExtraArea} puntos a cada una:
            el ejercicio es cuerpo y también mente.
          </span>
        </div>
      ) : null}

      <div className="field">
        <label>¿Cada cuánto?</label>
        <Segmented
          block
          label="Cadencia"
          value={kind}
          onChange={(v) => setKind(v)}
          options={[
            { value: 'daily', label: 'A diario' },
            { value: 'weekdays', label: 'Días' },
            { value: 'timesPerWeek', label: 'Semanal' },
            { value: 'timesPerMonth', label: 'Mensual' },
          ]}
        />
      </div>

      {kind === 'weekdays' ? (
        <div className="row row--wrap" role="group" aria-label="Días de la semana">
          {WEEKDAY_NAMES.map((label, index) => {
            const day = index + 1;
            const on = days.includes(day);
            return (
              <button
                key={day}
                type="button"
                className={on ? 'btn btn--sm coin--accent' : 'btn btn--sm btn--ghost'}
                style={{ width: 40, padding: 0 }}
                aria-pressed={on}
                onClick={() =>
                  setDays((prev) =>
                    prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
                  )
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : null}

      {kind === 'timesPerWeek' || kind === 'timesPerMonth' ? (
        <div className="field">
          <label htmlFor="habito-veces">
            ¿Cuántas veces {kind === 'timesPerWeek' ? 'por semana' : 'al mes'}?
          </label>
          <input
            id="habito-veces"
            className="input"
            type="number"
            min={1}
            max={kind === 'timesPerWeek' ? 7 : 31}
            value={times}
            onChange={(e) => setTimes(Math.max(1, Number(e.target.value)))}
          />
        </div>
      ) : null}

      <details>
        <summary className="t-sm t-dim" style={{ cursor: 'pointer', minHeight: 32 }}>
          ¿Se mide con un número?
        </summary>
        <div className="row" style={{ marginTop: 'var(--sp-3)' }}>
          <input
            className="input"
            type="number"
            min={0}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="Objetivo"
            aria-label="Objetivo numérico"
          />
          <input
            className="input"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="unidad"
            aria-label="Unidad"
          />
        </div>
        <p className="t-sm t-dim" style={{ marginTop: 'var(--sp-2)' }}>
          Pasar del objetivo suma 5 puntos extra. No pasar no resta nada.
        </p>
      </details>

      <button
        type="button"
        className="btn btn--primary btn--block"
        onClick={() => void guardar()}
        disabled={name.trim() === '' || areaId === ''}
      >
        {habit ? 'Guardar' : 'Añadir'}
      </button>

      {habit ? (
        <>
          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={() => {
              void archiveHabit(habit.id, habit.archivedAt === null);
              onClose();
            }}
          >
            {habit.archivedAt ? 'Recuperar' : 'Archivar'}
          </button>
          <p className="t-sm t-dim">
            Archivar no borra nada: el hábito sale de Hoy pero su historial sigue contando en el
            mapa de calor y en el rewind. Lo que hiciste, lo hiciste.
          </p>
        </>
      ) : null}
    </div>
  );
}
